import React from 'react';
import {
  ButtonIcon,
  IconChevronDown,
  IconChevronUp,
} from '@spotify-internal/encore-web';
import { UserState } from './types';
import { Link } from '../../ui';
import { RequireFeatureFlag } from '../../Auth';
import {
  CloseSideMenuButton,
  StyledSideMenu,
  SideMenuAction,
  SidemenuSeparator,
  SideMenuLink,
  SideMenuCategory,
  SideMenuCategoryTitle,
  SideMenuCategoryItem,
} from './NavSideMenu.styles';
import { USE_SEARCH } from './constants';

const TRACK_CAT = 'Side Nav';

type NavSideMenuProps = {
  userState: UserState;
  onClose(event: any): void;
};

type MenuState = {
  resources?: boolean;
  help?: boolean;
};

export const NavSideMenu: React.FC<NavSideMenuProps> = ({
  userState,
  onClose,
}) => {
  const showLogin = userState === 'loggedOut';
  const showLogout = userState === 'claimed' || userState === 'unclaimed';
  const showDashboard = userState === 'claimed';
  const showClaim = userState === 'unclaimed' || userState === 'loggedOut';
  const [menuState, setMenuState] = React.useState<MenuState>({});

  const toggleMenu = (key: keyof MenuState) => {
    setMenuState(cur => ({ ...cur, [key]: !cur[key] }));
  };

  return (
    <StyledSideMenu>
      <CloseSideMenuButton onClick={onClose} />

      <ul>
        {showClaim && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-claim"
                track={[TRACK_CAT, `Get Access (${userState})`]}
                href="/claim"
                prefetch={false}
              >
                Get access
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showLogout && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-logout"
                track={[TRACK_CAT, 'Log Out']}
                href={`https://accounts.spotify.com/logout?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                Log out
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showLogin && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-login"
                track={[TRACK_CAT, 'Log In']}
                href={`https://accounts.spotify.com/login?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                Log in
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showDashboard && (
          <li>
            <SideMenuAction>
              <Link
                track={[TRACK_CAT, 'Dashboard']}
                href="/c"
                prefetch={false}
                data-testid="navigation-sidemenu-dashboard"
              >
                Dashboard
              </Link>
            </SideMenuAction>
          </li>
        )}

        <li>
          <SidemenuSeparator />
        </li>

        <li>
          <SideMenuLink>
            <Link
              track={[TRACK_CAT, 'Features']}
              href="/features"
              data-testid="navigation-sidemenu-features"
            >
              Features
            </Link>
          </SideMenuLink>
        </li>

        <li>
          <SideMenuCategory>
            <ButtonIcon onClick={() => toggleMenu('help')}>
              <SideMenuCategoryTitle>
                Help
                {menuState.help ? (
                  <IconChevronUp iconSize={16} aria-label="Close help menu" />
                ) : (
                  <IconChevronDown iconSize={16} aria-label="Open help menu" />
                )}
              </SideMenuCategoryTitle>
            </ButtonIcon>
            {menuState.help && (
              <ul>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Guide']}
                      href="/guide"
                      data-testid="navigation-sidemenu-guide"
                    >
                      Guide
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'FAQs']}
                      href="/faq"
                      data-testid="navigation-sidemenu-faq"
                    >
                      FAQ
                    </Link>
                  </SideMenuCategoryItem>
                </li>
              </ul>
            )}
          </SideMenuCategory>
        </li>

        <li>
          <SideMenuCategory>
            <ButtonIcon onClick={() => toggleMenu('resources')}>
              <SideMenuCategoryTitle>
                Resources
                {menuState.resources ? (
                  <IconChevronUp
                    iconSize={16}
                    aria-label="Close resources menu"
                  />
                ) : (
                  <IconChevronDown
                    iconSize={16}
                    aria-label="Open resources menu"
                  />
                )}
              </SideMenuCategoryTitle>
            </ButtonIcon>
            {menuState.resources && (
              <ul>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-covid"
                      track={[TRACK_CAT, 'Covid-19']}
                      href="https://directory.byspotify.com/?ref=topnav"
                      prefetch={false}
                    >
                      Directory
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Videos']}
                      href="/videos"
                      data-testid="navigation-sidemenu-videos"
                    >
                      Videos
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Blog']}
                      href="/blog"
                      data-testid="navigation-sidemenu-blog"
                    >
                      News
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-events"
                      track={[TRACK_CAT, 'Events']}
                      href="/events"
                    >
                      Events
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-promocards"
                      track={[TRACK_CAT, 'Promo Cards']}
                      href="/promocards"
                    >
                      Promo Cards
                    </Link>
                  </SideMenuCategoryItem>
                </li>
              </ul>
            )}
          </SideMenuCategory>
        </li>

        <li>
          <SideMenuLink>
            <Link
              track={[TRACK_CAT, 'Providers']}
              href="/directory"
              data-testid="navigation-sidemenu-directory"
              prefetch={false}
            >
              Providers
            </Link>
          </SideMenuLink>
        </li>

        {USE_SEARCH && (
          <RequireFeatureFlag
            flag="s4a-frodor-test"
            fallback={<></>}
            waiting={<></>}
          >
            <li>
              <SideMenuLink>
                <Link
                  track={[TRACK_CAT, 'Search']}
                  href="/search"
                  data-testid="navigation-sidemenu-search"
                  prefetch={false}
                >
                  Search
                </Link>
              </SideMenuLink>
            </li>
          </RequireFeatureFlag>
        )}
      </ul>
    </StyledSideMenu>
  );
};
